/**
 * TimelineSuccessionsLayer Component
 *
 * Renders succession sub-rows below each crop that has a crop_successions record.
 * Each succession is the same phase/task data offset by (index * interval_days).
 *
 * Visual states (follow crop expansion — no independent accordion):
 * - Crop NOT expanded: thin 4px line per succession
 * - Crop IS expanded: collapsed-height rect per succession
 *   (doubled height when viewStrategy === 'both' to split phases/tasks)
 */

import React from 'react'
import { darken, useTheme } from '@mui/material'
import { useTimelineStaticContext } from '../../context/timeline-static-context'
import { useTimelineStyles } from '../../../hooks/use-timeline-styles'
import { PannableRect } from './pannable-rect'
import { PhaseIntersectionHandle } from './phase-intersection-handle'
import {
  EXPANDED_ROW_HEIGHT,
  SUCCESSION_THIN_HEIGHT,
  SUCCESSION_THIN_GAP,
  SUCCESSION_EXPANDED_HEIGHT,
  VERTICAL_DAY_TRACKER_SPACE,
} from '../../timeline-constants'
import { useOrgHoverContext } from '~/common/context/organization/org-context'

/**
 * TimelineSuccessionsLayer - Succession sub-rows for all crops
 *
 * @param {Object} props
 * @param {Array} props.crops - Transformed crop data (with phases, tasks, and succession)
 */
export const TimelineSuccessionsLayer = React.memo(({
  crops,
  onPanStart, panningChildId, isSuccessionPanning,
  onDragStart, draggingState, hoveredState, setHoveredState,
  onTaskDragStart, taskDraggingState, taskHoveredState, setTaskHoveredState,
}) => {
  const { hoveredOrg, hoveredTask } = useOrgHoverContext()
  const {
    expandedIds,
    weekWidth,
    getRowY,
    forceAllExpand,
    viewStrategy,
    showSuccessions,
    NOW_X,
    collapsedRowHeight,
    collapsedRowHeightGap,
  } = useTimelineStaticContext()

  const { getPhaseStyle, getTaskStyle, PHASE_BORDER_COLOR } = useTimelineStyles()
  const theme = useTheme()

  const BADGE_R = 7
  const BADGE_DASH = '3,2'

  if (!showSuccessions) return null

  const showPhases = viewStrategy === 'phases' || viewStrategy === 'both'
  const showTasks = viewStrategy === 'tasks' || viewStrategy === 'both'

  // When showing both phases and tasks, each succession row is doubled
  const heightMultiplier = viewStrategy === 'both' ? 2 : 1

  return (
    <>
      {crops.map((crop) => {
        if (crop.isOrgTaskRow) return null

        const succession = crop.succession
        if (!succession) return null

        const { count, children = [] } = succession
        if (!count || count < 1) return null

        const isMainExpanded = forceAllExpand || expandedIds.includes(crop.id)

        // Main row height (accounts for viewStrategy doubling)
        const baseMainRowH = isMainExpanded ? EXPANDED_ROW_HEIGHT : collapsedRowHeight
        const mainRowH = viewStrategy === 'both' ? baseMainRowH * 2 : baseMainRowH

        const mainRowY = getRowY(crop.id) + VERTICAL_DAY_TRACKER_SPACE * 8
        const successionBaseY = mainRowY + mainRowH

        // Succession row dimensions (follow crop state, no independent expand)
        const succRowH = isMainExpanded
          ? SUCCESSION_EXPANDED_HEIGHT * heightMultiplier
          : SUCCESSION_THIN_HEIGHT

        const barH = isMainExpanded
          ? SUCCESSION_EXPANDED_HEIGHT - collapsedRowHeightGap
          : SUCCESSION_THIN_HEIGHT

        // Sub-row height for 'both' layout (top = phases, bottom = tasks)
        const subRowH = isMainExpanded
          ? SUCCESSION_EXPANDED_HEIGHT  // half of succRowH when expanded in 'both'
          : SUCCESSION_THIN_HEIGHT

        return (
          <g key={`successions-${crop.id}`}>
            {children.map((child, i) => {
              const successionIndex = i + 1  // 1-based
              const succKey = `${crop.id}-${successionIndex}`

              // Y position: accumulate preceding succession row heights
              const rowStep = isMainExpanded
                ? SUCCESSION_EXPANDED_HEIGHT * heightMultiplier
                : SUCCESSION_THIN_HEIGHT + SUCCESSION_THIN_GAP

              const succRowY = successionBaseY + i * rowStep

              // Use actual child crop phases/tasks (already at correct absolute positions)
              const childPhases = child.phases || []
              const childTasks = child.tasks || []

              return (
                <g key={succKey}>
                  {/* Phase rects — child's own schedule data, no offset needed */}
                  {showPhases && childPhases.map((phase, pidx) => {
                    const s = getPhaseStyle(phase.type)
                    const x = phase.start * weekWidth
                    const phaseWidth = phase.len * weekWidth

                    // Center phase rect in top sub-row (or full row if not 'both')
                    const barY = viewStrategy === 'both' && isMainExpanded
                      ? succRowY + (subRowH / 2) - (barH / 2)
                      : succRowY + (succRowH / 2) - (barH / 2)

                    const isParentOrgHovered = crop.organization_id === hoveredOrg?.id
                    const fillColor = isParentOrgHovered ? darken(s.color, 0.2) : s.color
                    const strokeW = isParentOrgHovered ? 2 : 0.25
  
                    const isPannable = crop.interactive && isMainExpanded && !child.isPreview
                    const phaseOpacity = child.isPreview ? 0.45 : 0.75
                    const phaseDash = child.isPreview ? '4,2' : undefined
                    const phaseStrokeW = child.isPreview ? 1 : strokeW
                    return (
                      <g key={`succ-phase-${succKey}-${pidx}`}>
                        {isPannable ? (
                          <PannableRect
                            x={x}
                            y={barY}
                            width={phaseWidth}
                            height={barH}
                            fill={fillColor}
                            rx={4}
                            stroke={PHASE_BORDER_COLOR}
                            strokeWidth={phaseStrokeW}
                            opacity={phaseOpacity}
                            isPanning={isSuccessionPanning && panningChildId === child.id}
                            onPanStart={(clientX) => onPanStart(child.id, childPhases, childTasks, clientX)}
                          />
                        ) : (
                          <rect
                            x={x}
                            y={barY}
                            width={phaseWidth}
                            height={barH}
                            fill={fillColor}
                            rx={isMainExpanded ? 4 : 2}
                            stroke={PHASE_BORDER_COLOR}
                            strokeWidth={phaseStrokeW}
                            strokeDasharray={phaseDash}
                            opacity={phaseOpacity}
                          />
                        )}
                      </g>
                    )
                  })}

                  {/* Task rects — child's own task data, no offset needed */}
                  {showTasks && childTasks.map((task, tidx) => {
                    const s = getTaskStyle(task.type)
                    const x = task.start * weekWidth
                    const taskWidth = task.len * weekWidth
                    const isMultiSubmit = task.single_completion === false

                    // Center task rect in bottom sub-row (or full row if not 'both')
                    const taskBarY = viewStrategy === 'both' && isMainExpanded
                      ? succRowY + subRowH + (subRowH / 2) - (barH / 2)
                      : succRowY + (succRowH / 2) - (barH / 2)

                    const isParentOrgHovered = crop.organization_id === hoveredOrg?.id
                    const isTaskHovered = task.id === hoveredTask
                    const fillColor = isParentOrgHovered || isTaskHovered ? darken(s.color, 0.2) : s.color
                    const strokeW = isParentOrgHovered || isTaskHovered ? 2 : 0.25

                    const isCompleted = task.state === 'completed'
                    const completedAtWeeks = task.completed_at_weeks ?? null

                    const isTaskPannable = crop.interactive && isMainExpanded && !isCompleted && !child.isPreview
                    const taskOpacity = child.isPreview ? 0.45 : 0.75
                    const taskDash = child.isPreview ? '4,2' : undefined
                    const taskStrokeW = child.isPreview ? 1 : strokeW
                    return (
                      <g key={`succ-task-${succKey}-${tidx}`}>
                        {isTaskPannable ? (
                          <PannableRect
                            x={x}
                            y={taskBarY}
                            width={taskWidth}
                            height={barH}
                            fill={fillColor}
                            rx={4}
                            stroke={PHASE_BORDER_COLOR}
                            strokeWidth={taskStrokeW}
                            opacity={taskOpacity}
                            isPanning={isSuccessionPanning && panningChildId === child.id}
                            onPanStart={(clientX) => onPanStart(child.id, childPhases, childTasks, clientX)}
                          />
                        ) : (
                          <>
                            <rect
                              x={x}
                              y={taskBarY}
                              width={taskWidth}
                              height={barH}
                              fill={fillColor}
                              rx={isMainExpanded ? 4 : 2}
                              stroke={PHASE_BORDER_COLOR}
                              strokeWidth={taskStrokeW}
                              strokeDasharray={taskDash}
                              opacity={taskOpacity}
                            />
                            {isMultiSubmit && (
                              <rect
                                x={x + 3}
                                y={taskBarY + 3}
                                width={taskWidth - 6}
                                height={barH - 6}
                                fill="none"
                                rx={4}
                                stroke="white"
                                strokeWidth={1.5}
                                style={{ pointerEvents: 'none' }}
                              />
                            )}
                          </>
                        )}
                        {isCompleted && completedAtWeeks != null && (() => {
                          const today = new Date().toDateString()
                          const completedDay = task.completed_at ? new Date(task.completed_at).toDateString() : null
                          const rawX = (completedDay === today && NOW_X != null) ? NOW_X : completedAtWeeks * weekWidth
                          const cx = Math.max(x + BADGE_R + 2, Math.min(x + taskWidth - BADGE_R - 2, rawX))
                          const circleY = taskBarY - BADGE_R - 2
                          return (
                            <g style={{ pointerEvents: 'none' }}>
                              <line
                                x1={cx} y1={taskBarY}
                                x2={cx} y2={taskBarY + barH}
                                stroke={theme.palette.success.main}
                                strokeWidth={1.5}
                                strokeDasharray={BADGE_DASH}
                                opacity={0.85}
                              />
                              <circle cx={cx} cy={circleY} r={BADGE_R} fill={theme.palette.success.main} opacity={0.9} />
                              <text x={cx} y={circleY + 1} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize={9} fontWeight="bold" style={{ userSelect: 'none' }}>✓</text>
                            </g>
                          )
                        })()}
                      </g>
                    )
                  })}

                  {/* Task drag handles for this succession child */}
                  {crop.interactive && isMainExpanded && childTasks.length > 0 && (() => {
                    const taskBarY = viewStrategy === 'both'
                      ? succRowY + subRowH + (subRowH / 2) - (barH / 2)
                      : succRowY + (succRowH / 2) - (barH / 2)
                    const makeTaskDragStart = (intersectionIndex, clientX) =>
                      onTaskDragStart?.(child.id, childTasks.filter(t => t.state !== 'completed'), intersectionIndex, clientX)
                    const makeTaskHover = (intersectionIndex) =>
                      setTaskHoveredState?.(intersectionIndex != null ? { childId: child.id, intersectionIndex } : null)
                    const isTaskDraggingHandle = (idx) => {
                      if (!taskDraggingState || taskDraggingState.childId !== child.id) return false
                      const a = taskDraggingState.intersectionIndex
                      if (typeof idx === 'object' && typeof a === 'object') return a?.type === idx?.type && a?.taskIndex === idx?.taskIndex
                      return a === idx
                    }
                    const isTaskHoveredHandle = (idx) => {
                      if (!taskHoveredState || taskHoveredState.childId !== child.id) return false
                      const a = taskHoveredState.intersectionIndex
                      if (typeof idx === 'object' && typeof a === 'object') return a?.type === idx?.type && a?.taskIndex === idx?.taskIndex
                      return a === idx
                    }
                    const nonCompletedTasks = childTasks.filter(t => t.state !== 'completed')
                    if (nonCompletedTasks.length === 0) return null
                    return (
                      <g key={`succ-task-handles-${succKey}`}>
                        {nonCompletedTasks.map((task, tidx) => {
                          const startId = { type: 'start', taskIndex: tidx }
                          const endId = { type: 'end', taskIndex: tidx }
                          return (
                            <g key={`succ-task-handle-${succKey}-${tidx}`}>
                              <PhaseIntersectionHandle
                                x={task.start * weekWidth}
                                y={taskBarY}
                                height={barH}
                                intersectionIndex={startId}
                                onDragStart={makeTaskDragStart}
                                isDragging={isTaskDraggingHandle(startId)}
                                isHovered={isTaskHoveredHandle(startId)}
                                onHover={makeTaskHover}
                                diamondOffset={0}
                              />
                              <PhaseIntersectionHandle
                                x={(task.start + task.len) * weekWidth}
                                y={taskBarY}
                                height={barH}
                                intersectionIndex={endId}
                                onDragStart={makeTaskDragStart}
                                isDragging={isTaskDraggingHandle(endId)}
                                isHovered={isTaskHoveredHandle(endId)}
                                onHover={makeTaskHover}
                                diamondOffset={0}
                              />
                            </g>
                          )
                        })}
                      </g>
                    )
                  })()}

                  {/* Phase drag handles for this succession child */}
                  {crop.interactive && isMainExpanded && childPhases.length > 0 && (() => {
                    const phaseBarY = viewStrategy === 'both'
                      ? succRowY + (subRowH / 2) - (barH / 2)
                      : succRowY + (succRowH / 2) - (barH / 2)
                    const firstPhase = childPhases[0]
                    const lastPhase = childPhases[childPhases.length - 1]
                    const makeDragStart = (intersectionIndex, clientX) =>
                      onDragStart?.(child.id, childPhases, intersectionIndex, clientX)
                    const makeHover = (intersectionIndex) =>
                      setHoveredState?.(intersectionIndex != null ? { childId: child.id, intersectionIndex } : null)
                    const isDraggingHandle = (idx) =>
                      draggingState?.childId === child.id && draggingState?.intersectionIndex === idx
                    const isHoveredHandle = (idx) =>
                      hoveredState?.childId === child.id && hoveredState?.intersectionIndex === idx
                    return (
                      <g key={`succ-handles-${succKey}`}>
                        <PhaseIntersectionHandle
                          x={firstPhase.start * weekWidth}
                          y={phaseBarY}
                          height={barH}
                          intersectionIndex="start"
                          onDragStart={makeDragStart}
                          isDragging={isDraggingHandle('start')}
                          isHovered={isHoveredHandle('start')}
                          onHover={makeHover}
                          diamondOffset={0}
                        />
                        {childPhases.map((phase, pidx) => {
                          if (pidx === childPhases.length - 1) return null
                          const ix = (phase.start + phase.len) * weekWidth
                          return (
                            <PhaseIntersectionHandle
                              key={`succ-handle-${succKey}-${pidx}`}
                              x={ix}
                              y={phaseBarY}
                              height={barH}
                              intersectionIndex={pidx}
                              onDragStart={makeDragStart}
                              isDragging={isDraggingHandle(pidx)}
                              isHovered={isHoveredHandle(pidx)}
                              onHover={makeHover}
                              diamondOffset={0}
                            />
                          )
                        })}
                        <PhaseIntersectionHandle
                          x={(lastPhase.start + lastPhase.len) * weekWidth}
                          y={phaseBarY}
                          height={barH}
                          intersectionIndex="end"
                          onDragStart={makeDragStart}
                          isDragging={isDraggingHandle('end')}
                          isHovered={isHoveredHandle('end')}
                          onHover={makeHover}
                          diamondOffset={0}
                        />
                      </g>
                    )
                  })()}
                </g>
              )
            })}
          </g>
        )
      })}
    </>
  )
})
